import React, { useState } from "react";
import { useLang } from "../context/LangContext";
import { t } from "../lib/i18n";
import { api } from "../lib/api";
import { Phone, EnvelopeSimple, MapPin } from "@phosphor-icons/react";
import { toast } from "sonner";
import SEO from "../components/SEO";

export default function Contact() {
  const { lang } = useLang();
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [busy, setBusy] = useState(false);
  const change = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    try {
      setBusy(true);
      await api.post("/contact", form);
      toast.success(lang === "gr" ? "Λάβαμε το μήνυμά σας!" : "Message received!");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch {
      toast.error(lang === "gr" ? "Αποτυχία" : "Failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-20" data-testid="contact-page">
      <SEO
        title={{ en: "Contact MP Sound", gr: "Επικοινωνία MP Sound" }}
        description={{ en: "Get in touch with MP Sound — pro audio, lighting and AV in Cyprus. Geri 2200.", gr: "Επικοινωνήστε με την MP Sound — επαγγελματικός ήχος και φωτισμός στην Κύπρο." }}
        path="/contact"
      />
      <div className="grid lg:grid-cols-2 gap-16">
        <div>
          <div className="label-tag mb-4 slash">{t("sections.contact_eyebrow", lang)}</div>
          <h1 className="display-font text-5xl sm:text-7xl font-black uppercase leading-[0.9] tracking-tighter">
            {lang === "gr" ? <>Ακούμε <span className="neon-text">πάντα.</span></> : <>Always <span className="neon-text">listening.</span></>}
          </h1>
          <p className="text-gray-300 mt-6 max-w-md">
            {lang === "gr"
              ? "Στείλτε μας μήνυμα για ενοικίαση, εγκατάσταση, service ή οποιαδήποτε ερώτηση. Απαντάμε μέσα σε λίγες ώρες."
              : "Drop us a message about rental, install, service or anything else. We reply within a few hours."}
          </p>

          <div className="space-y-5 mt-12">
            <div className="flex items-start gap-4">
              <Phone size={22} weight="duotone" className="text-[#ccff00] mt-1" />
              <div>
                <div className="label-tag">{lang === "gr" ? "Τηλέφωνο" : "Phone"}</div>
                <a href="tel:+35722574428" className="text-lg font-semibold" data-testid="contact-phone-1">+357 22 574428</a><br/>
                <a href="tel:+35799574428" className="text-lg font-semibold" data-testid="contact-phone-2">+357 99 574428</a>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <EnvelopeSimple size={22} weight="duotone" className="text-[#ccff00] mt-1" />
              <div>
                <div className="label-tag">Email</div>
                <a href="mailto:info@mpsound.eu" className="text-lg font-semibold" data-testid="contact-email">info@mpsound.eu</a>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <MapPin size={22} weight="duotone" className="text-[#ccff00] mt-1" />
              <div>
                <div className="label-tag">{lang === "gr" ? "Διεύθυνση" : "Location"}</div>
                <div className="text-lg">Cyprus</div>
                <div className="text-gray-400 text-sm">A.A. Model Power Ltd</div>
              </div>
            </div>
          </div>
        </div>

        <form onSubmit={submit} className="card-dark p-8" data-testid="contact-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <input required placeholder={t("forms.name", lang)} className="input-dark" value={form.name} onChange={change("name")} data-testid="contact-name" />
            <input required type="email" placeholder={t("forms.email", lang)} className="input-dark" value={form.email} onChange={change("email")} data-testid="contact-email-input" />
            <input placeholder={t("forms.phone", lang)} className="input-dark" value={form.phone} onChange={change("phone")} data-testid="contact-phone-input" />
            <input placeholder={t("forms.subject", lang)} className="input-dark" value={form.subject} onChange={change("subject")} data-testid="contact-subject" />
            <textarea required placeholder={t("forms.message", lang)} className="input-dark sm:col-span-2 min-h-[160px]" value={form.message} onChange={change("message")} data-testid="contact-message" />
          </div>
          <button type="submit" disabled={busy} className="btn-primary mt-6 w-full justify-center" data-testid="contact-submit">
            {busy ? "..." : t("cta.send", lang)}
          </button>
        </form>
      </div>

      {/* Google Maps embed */}
      <div className="mt-20" data-testid="contact-map-wrap">
        <div className="label-tag mb-3 slash">{lang === "gr" ? "Βρείτε μας" : "Find us"}</div>
        <h2 className="display-font text-3xl sm:text-4xl font-black uppercase mb-8">
          {lang === "gr" ? "Geri, Κύπρος" : "Geri, Cyprus"}
        </h2>
        <div className="border border-[#2a2a35] overflow-hidden">
          <iframe
            title="MP Sound location"
            src="https://www.google.com/maps?q=3CM3%2BJ79+Geri+Cyprus&output=embed"
            width="100%"
            height="450"
            style={{ border: 0, filter: "invert(0.92) hue-rotate(180deg)" }}
            allowFullScreen=""
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            data-testid="contact-map-iframe"
          />
        </div>
        <a
          href="https://www.google.com/maps/place/3CM3%2BJ79+Geri+2200"
          target="_blank" rel="noreferrer"
          className="inline-flex items-center gap-2 mono-font text-xs uppercase tracking-[0.2em] text-[#ccff00] mt-4"
          data-testid="open-maps-link"
        >
          {lang === "gr" ? "Άνοιγμα στο Google Maps" : "Open in Google Maps"} →
        </a>
      </div>
    </div>
  );
}
